import { api } from '../../app/api';

import type {
    ActiveOrderFlag,
    OrderFlag,
    OrderFlagForm,
    OrderFlagTag,
    OrderFlagTagForm,
} from './orderFlags.schema';
import {
    ActiveOrderFlagListResponseSchema,
    OrderFlagListResponseSchema,
    OrderFlagResponseSchema,
    OrderFlagTagListResponseSchema,
    OrderFlagTagResponseSchema,
} from './orderFlags.schema';

type ListArgs = {
    start?: number;
    limit?: number;
    search?: string;
};

const orderFlagsApi = api.injectEndpoints({
    endpoints: (builder) => ({
        // ---- Tag catalog ----
        listOrderFlagTags: builder.query<OrderFlagTag[], string | void>({
            query: (search) => ({
                url: '/v2/order-flag-tags',
                method: 'GET',
                params: { q: search || undefined },
            }),
            transformResponse: (raw: unknown) => OrderFlagTagListResponseSchema.parse(raw).data,
            providesTags: (result) =>
                result
                    ? [
                          ...result.map((t) => ({ type: 'OrderFlagTag' as const, id: t.id })),
                          { type: 'OrderFlagTag' as const, id: 'LIST' },
                      ]
                    : [{ type: 'OrderFlagTag' as const, id: 'LIST' }],
        }),

        createOrderFlagTag: builder.mutation<OrderFlagTag, OrderFlagTagForm>({
            query: (body) => ({
                url: '/v2/order-flag-tags',
                method: 'POST',
                body: serializeTag(body),
            }),
            transformResponse: (raw: unknown) => OrderFlagTagResponseSchema.parse(raw).data,
            invalidatesTags: [
                { type: 'OrderFlagTag', id: 'LIST' },
                { type: 'OrderFlagActive', id: 'LIST' },
            ],
        }),

        updateOrderFlagTag: builder.mutation<OrderFlagTag, { id: number; values: OrderFlagTagForm }>({
            query: ({ id, values }) => ({
                url: '/v2/order-flag-tags/[id]',
                method: 'PUT',
                query: { id },
                body: serializeTag(values),
            }),
            transformResponse: (raw: unknown) => OrderFlagTagResponseSchema.parse(raw).data,
            invalidatesTags: (_r, _e, { id }) => [
                { type: 'OrderFlagTag', id },
                { type: 'OrderFlagTag', id: 'LIST' },
                { type: 'OrderFlagActive', id: 'LIST' },
            ],
        }),

        deleteOrderFlagTag: builder.mutation<void, number>({
            query: (id) => ({
                url: '/v2/order-flag-tags/[id]',
                method: 'DELETE',
                query: { id },
            }),
            invalidatesTags: (_r, _e, id) => [
                { type: 'OrderFlagTag', id },
                { type: 'OrderFlagTag', id: 'LIST' },
                { type: 'OrderFlag', id: 'LIST' },
                { type: 'OrderFlagActive', id: 'LIST' },
            ],
        }),

        // ---- Blocklist entries ----
        listOrderFlags: builder.query<{ data: OrderFlag[]; count: number }, ListArgs | void>({
            query: (args) => {
                const { start = 0, limit = 100, search } = args ?? {};
                return {
                    url: '/v2/order-flags',
                    method: 'GET',
                    params: {
                        _start: start,
                        _end: start + limit,
                        _sort: 'createdAt',
                        _order: 'DESC',
                        q: search || undefined,
                    },
                };
            },
            transformResponse: (raw: unknown) => {
                const parsed = OrderFlagListResponseSchema.parse(raw);
                return { data: parsed.data, count: parsed.count };
            },
            providesTags: (result) =>
                result
                    ? [
                          ...result.data.map((f) => ({ type: 'OrderFlag' as const, id: f.id })),
                          { type: 'OrderFlag' as const, id: 'LIST' },
                      ]
                    : [{ type: 'OrderFlag' as const, id: 'LIST' }],
        }),

        createOrderFlag: builder.mutation<OrderFlag, OrderFlagForm>({
            query: (body) => ({
                url: '/v2/order-flags',
                method: 'POST',
                body: serializeEntry(body),
            }),
            transformResponse: (raw: unknown) => OrderFlagResponseSchema.parse(raw).data,
            invalidatesTags: [
                { type: 'OrderFlag', id: 'LIST' },
                { type: 'OrderFlagActive', id: 'LIST' },
            ],
        }),

        updateOrderFlag: builder.mutation<OrderFlag, { id: number; values: OrderFlagForm }>({
            query: ({ id, values }) => ({
                url: '/v2/order-flags/[id]',
                method: 'PUT',
                query: { id },
                body: serializeEntry(values),
            }),
            transformResponse: (raw: unknown) => OrderFlagResponseSchema.parse(raw).data,
            invalidatesTags: (_r, _e, { id }) => [
                { type: 'OrderFlag', id },
                { type: 'OrderFlag', id: 'LIST' },
                { type: 'OrderFlagActive', id: 'LIST' },
            ],
        }),

        deleteOrderFlag: builder.mutation<void, number>({
            query: (id) => ({
                url: '/v2/order-flags/[id]',
                method: 'DELETE',
                query: { id },
            }),
            invalidatesTags: (_r, _e, id) => [
                { type: 'OrderFlag', id },
                { type: 'OrderFlag', id: 'LIST' },
                { type: 'OrderFlagActive', id: 'LIST' },
            ],
        }),

        // The compact set the order badge matches against. Long-lived in cache;
        // mutations above invalidate it.
        listActiveOrderFlags: builder.query<ActiveOrderFlag[], void>({
            query: () => ({ url: '/v2/order-flags/active', method: 'GET' }),
            transformResponse: (raw: unknown) => ActiveOrderFlagListResponseSchema.parse(raw).data,
            providesTags: [{ type: 'OrderFlagActive', id: 'LIST' }],
        }),
    }),
});

function serializeTag(values: OrderFlagTagForm) {
    return {
        code: values.code,
        label: values.label,
        description: values.description || null,
        color: values.color ? values.color : null,
        active: values.active,
    };
}

function serializeEntry(values: OrderFlagForm) {
    return {
        email: values.email ? values.email.trim() : null,
        phone: values.phone ? values.phone.trim() : null,
        tagId: values.tagId,
        note: values.note || null,
    };
}

export const {
    useListOrderFlagTagsQuery,
    useCreateOrderFlagTagMutation,
    useUpdateOrderFlagTagMutation,
    useDeleteOrderFlagTagMutation,
    useListOrderFlagsQuery,
    useCreateOrderFlagMutation,
    useUpdateOrderFlagMutation,
    useDeleteOrderFlagMutation,
    useListActiveOrderFlagsQuery,
} = orderFlagsApi;
